import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Page from '../../components/Page';
import { auth } from '../../firebase';
import { BASE_HOST, getAccountAPIHeader } from '../../utils/urlConst';

export default function CreativeUploads() {
  const [user] = useAuthState(auth);
  const [options, setOptions] = useState(null);
  const [optionsAttempt, setOptionsAttempt] = useState(0);
  const [file, setFile] = useState(null);
  const [offer, setOffer] = useState('');
  const [creativeType, setCreativeType] = useState('email creative');
  const [purpose, setPurpose] = useState('email');
  const [code, setCode] = useState('');
  const [url, setUrl] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [source, setSource] = useState('pdf');
  const [review, setReview] = useState(null);
  const [names, setNames] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const signedIn = !!user;
  useEffect(() => {
    if (!signedIn) return undefined;
    let current = true;
    async function loadOptions() {
      try {
        const config = getAccountAPIHeader(await user.getIdToken());
        const { data } = await axios.get(`${BASE_HOST}/api/creative-uploads/options`, config);
        if (current) setOptions(data);
      } catch {
        if (current) setError('Creative options could not be loaded. Retry to continue.');
      }
    }
    loadOptions();
    return () => {
      current = false;
    };
  }, [user, signedIn, optionsAttempt]);

  const reset = () => {
    setReview(null);
    setResults(null);
    setNames([]);
    setAcknowledged(false);
    setDirty(false);
    setError('');
  };
  const acceptReview = (data) => {
    setReview(data);
    setNames(
      data.creatives.map((creative) =>
        data.purpose === 'sms' ? creative.display_name.replace(/^SMS — /, '') : creative.display_name
      )
    );
    setAcknowledged(false);
    setDirty(false);
    setResults(null);
  };
  const settings = options?.types[creativeType];
  const input = settings?.input || 'email';
  const fileInput = input === 'email' || input === 'file';
  let accept = options?.file_extensions.map((extension) => `.${extension}`).join(',') || '';
  if (purpose === 'sms') accept = '.docx';
  else if (input === 'email') accept = source === 'html' ? '.html,.htm' : `.${source}`;
  let hasContent = !!code.trim();
  if (fileInput) hasContent = !!file;
  else if (input === 'url') hasContent = !!url.trim();
  const canAnalyze =
    !!options &&
    !!user &&
    /^[1-9][0-9]*$/.test(offer) &&
    hasContent &&
    (!settings?.dimensions ||
      [width, height].every((value) => (!settings.required_dimensions && value === '') || /^[1-9][0-9]*$/.test(value)));
  let step = 0;
  if (results) step = 2;
  else if (review) step = 1;
  let templateFormat = 'email-html';
  if (purpose === 'sms') templateFormat = 'sms-docx';
  else if (source === 'pdf') templateFormat = 'email-pdf';
  let fileHelp = 'Original file bytes are preserved.';
  if (purpose === 'sms') fileHelp = 'SMS DOCX document';
  else if (input === 'email') fileHelp = 'Choose the matching source format above.';

  async function request(operation) {
    setBusy(true);
    setError('');
    try {
      const config = getAccountAPIHeader(await user.getIdToken());
      const body = new FormData();
      if (fileInput) body.append('file', file);
      body.append('offer_id', offer);
      body.append('creative_type', creativeType);
      body.append('purpose', purpose);
      if (input === 'code') body.append('code', code);
      if (input === 'url') body.append('url', url);
      if (settings?.dimensions) {
        if (width) body.append('width', width);
        if (height) body.append('height', height);
      }
      if (names.length) body.append('display_names', JSON.stringify(names));
      if (operation === 'upload')
        body.append(
          'acknowledged_warning_codes',
          JSON.stringify(acknowledged ? review.warnings.map((warning) => warning.code) : [])
        );
      const { data } = await axios.post(`${BASE_HOST}/api/creative-uploads/${operation}`, body, config);
      if (operation === 'analyze') acceptReview(data);
      else setResults(data.results);
    } catch (failure) {
      const data = failure.response?.data;
      if (failure.response?.status === 409 && data?.code === 'review_required') acceptReview(data);
      setError(data?.error || 'Request failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function downloadTemplate(format) {
    setBusy(true);
    setError('');
    try {
      const config = getAccountAPIHeader(await user.getIdToken());
      const { data } = await axios.get(`${BASE_HOST}/api/creative-uploads/template?format=${format}`, {
        ...config,
        responseType: 'blob',
      });
      const objectUrl = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `creative-upload-template.${
        { 'email-pdf': 'pdf', 'email-html': 'html', 'sms-docx': 'docx' }[format]
      }`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      setError('Template download failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title="Creative Uploads">
      <Container maxWidth="md">
        <Stack spacing={3} sx={{ pb: 5 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Creative Uploads
            </Typography>
            <Typography color="text.secondary">
              Prepare creatives for HasOffers, review the checks, then confirm your upload.
            </Typography>
          </Box>
          <Stepper activeStep={step} alternativeLabel>
            {['Choose creative', 'Review & confirm', 'Results'].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {busy && <LinearProgress aria-label="Working" />}
          {error && <Alert severity="error">{error}</Alert>}
          {!signedIn && <Alert severity="info">Sign in to analyze and upload creatives.</Alert>}
          {!options && signedIn && (
            <Button onClick={() => setOptionsAttempt((value) => value + 1)}>Retry loading options</Button>
          )}
          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
              <Typography variant="h6">Creative details</Typography>
              <TextField
                label="HasOffers offer ID"
                value={offer}
                disabled={busy}
                inputProps={{ 'aria-label': 'HasOffers offer ID', inputMode: 'numeric' }}
                onChange={(event) => {
                  reset();
                  setOffer(event.target.value);
                }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Creative type"
                  value={creativeType}
                  disabled={busy || !options}
                  SelectProps={{ native: true, inputProps: { 'aria-label': 'Creative type' } }}
                  onChange={(event) => {
                    reset();
                    setFile(null);
                    setCreativeType(event.target.value);
                    setPurpose(event.target.value === 'email creative' ? 'email' : 'other');
                  }}
                >
                  {Object.entries(options?.types || { 'email creative': { label: 'Email creative' } }).map(
                    ([value, setting]) => (
                      <option value={value} key={value}>
                        {setting.label}
                      </option>
                    )
                  )}
                </TextField>
                {(creativeType === 'file' || creativeType === 'email creative') && (
                  <TextField
                    select
                    fullWidth
                    label="Purpose"
                    value={purpose}
                    disabled={busy}
                    SelectProps={{ native: true, inputProps: { 'aria-label': 'Purpose' } }}
                    onChange={(event) => {
                      reset();
                      setFile(null);
                      setPurpose(event.target.value);
                      if (event.target.value !== 'email') setCreativeType('file');
                    }}
                  >
                    <option value="other">Other file</option>
                    <option value="email">Email document</option>
                    <option value="sms">SMS</option>
                  </TextField>
                )}
                {creativeType === 'email creative' && (
                  <TextField
                    select
                    fullWidth
                    label="Source format"
                    value={source}
                    disabled={busy}
                    SelectProps={{ native: true, inputProps: { 'aria-label': 'Source format' } }}
                    onChange={(event) => {
                      reset();
                      setFile(null);
                      setSource(event.target.value);
                    }}
                  >
                    <option value="pdf">PDF</option>
                    <option value="html">HTML</option>
                    <option value="docx">DOCX</option>
                  </TextField>
                )}
              </Stack>
              {purpose === 'sms' && (
                <Alert severity="info">
                  The document is uploaded unchanged. Its display name always starts with “SMS — ” so the email importer
                  skips it.
                </Alert>
              )}
              {fileInput && (
                <TextField
                  key={`${creativeType}-${purpose}-${source}`}
                  type="file"
                  disabled={busy}
                  inputProps={{ accept, 'aria-label': 'Creative file' }}
                  onChange={(event) => {
                    reset();
                    setFile(event.target.files[0] || null);
                  }}
                  helperText={fileHelp}
                />
              )}
              {input === 'code' && (
                <TextField
                  label="Creative code"
                  multiline
                  minRows={6}
                  value={code}
                  disabled={busy}
                  inputProps={{ 'aria-label': 'Creative code' }}
                  onChange={(event) => {
                    reset();
                    setCode(event.target.value);
                  }}
                />
              )}
              {input === 'url' && (
                <TextField
                  label="XML feed URL"
                  value={url}
                  disabled={busy}
                  inputProps={{ 'aria-label': 'XML feed URL' }}
                  helperText="Passed to HasOffers without fetching the URL."
                  onChange={(event) => {
                    reset();
                    setUrl(event.target.value);
                  }}
                />
              )}
              {settings?.dimensions && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Width"
                    required={!!settings.required_dimensions}
                    helperText={!settings.required_dimensions && 'Optional; HasOffers can detect this.'}
                    value={width}
                    disabled={busy}
                    InputProps={{
                      inputProps: { 'aria-label': 'Width', inputMode: 'numeric' },
                      endAdornment: <InputAdornment position="end">px</InputAdornment>,
                    }}
                    onChange={(event) => {
                      reset();
                      setWidth(event.target.value);
                    }}
                  />
                  <TextField
                    label="Height"
                    required={!!settings.required_dimensions}
                    helperText={!settings.required_dimensions && 'Optional; HasOffers can detect this.'}
                    value={height}
                    disabled={busy}
                    InputProps={{
                      inputProps: { 'aria-label': 'Height', inputMode: 'numeric' },
                      endAdornment: <InputAdornment position="end">px</InputAdornment>,
                    }}
                    onChange={(event) => {
                      reset();
                      setHeight(event.target.value);
                    }}
                  />
                </Stack>
              )}
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button variant="contained" disabled={busy || !canAnalyze} onClick={() => request('analyze')}>
                  Analyze
                </Button>
                {(input === 'email' || purpose === 'sms') && (
                  <Button disabled={busy || !user} onClick={() => downloadTemplate(templateFormat)}>
                    {input === 'email' && source === 'docx' ? 'Download HTML Template' : 'Download Template'}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Card>
          {review && !results && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5">Review creatives</Typography>
                <Typography>
                  Destination: {review.destination_offer.id} — {review.destination_offer.name}
                </Typography>
                <Typography color="text.secondary">
                  Document: {review.document_offer.ids.join(', ') || 'No offer ID'} —{' '}
                  {review.document_offer.names.join(', ') || 'No offer name'}
                </Typography>
              </Box>
              {!!review.warnings.filter((warning) => warning.code.startsWith('document:')).length && (
                <Alert severity="warning">
                  <Typography variant="subtitle2">Offer checks</Typography>
                  {review.warnings
                    .filter((warning) => warning.code.startsWith('document:'))
                    .map((warning) => (
                      <Typography variant="body2" key={warning.code}>
                        {warning.message}
                      </Typography>
                    ))}
                </Alert>
              )}
              {review.creatives.map((creative, index) => (
                <Card key={creative.number} sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6">Creative {creative.number}</Typography>
                      <Chip size="small" label={creative.type || 'email creative'} />
                    </Stack>
                    <TextField
                      label={`Display name ${creative.number}`}
                      value={names[index] || ''}
                      disabled={busy}
                      InputProps={{
                        inputProps: {
                          maxLength: review.purpose === 'sms' ? 249 : 255,
                          'aria-label': `Display name ${creative.number}`,
                        },
                        startAdornment:
                          review.purpose === 'sms' ? (
                            <InputAdornment position="start">SMS — </InputAdornment>
                          ) : undefined,
                      }}
                      onChange={(event) => {
                        setNames(names.map((name, i) => (i === index ? event.target.value : name)));
                        setDirty(true);
                        setAcknowledged(false);
                      }}
                    />
                    {creative.type === 'email creative' || (!creative.type && input === 'email') ? (
                      <>
                        <Typography>Subjects: {creative.subjects.join(' • ') || 'Missing'}</Typography>
                        <Typography>Preheader: {creative.preheader || 'Missing'}</Typography>
                      </>
                    ) : null}
                    {creative.file && (
                      <Typography>
                        {creative.file.filename} — {creative.file.size} bytes. Uploaded unchanged.
                      </Typography>
                    )}
                    {creative.url && <Typography sx={{ overflowWrap: 'anywhere' }}>{creative.url}</Typography>}
                    {!!review.warnings.filter((warning) => warning.code.startsWith(`creative:${creative.number}:`))
                      .length && (
                      <Alert severity="warning">
                        <Typography variant="subtitle2">Review before upload</Typography>
                        {review.warnings
                          .filter((warning) => warning.code.startsWith(`creative:${creative.number}:`))
                          .map((warning) => (
                            <Typography variant="body2" key={warning.code}>
                              {warning.message}
                            </Typography>
                          ))}
                      </Alert>
                    )}
                    {!!creative.corrections.length && (
                      <Alert severity="info">
                        <Typography variant="subtitle2">Automatic corrections</Typography>
                        {creative.corrections.map((correction) => (
                          <Typography variant="body2" key={correction}>
                            {correction}
                          </Typography>
                        ))}
                      </Alert>
                    )}
                    {creative.html && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>Preview prepared creative</AccordionSummary>
                        <AccordionDetails>
                          <iframe
                            title={`Creative ${creative.number} preview`}
                            sandbox=""
                            referrerPolicy="no-referrer"
                            srcDoc={creative.body_html || creative.html}
                            style={{ width: '100%', height: 400, border: 0 }}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Stack>
                </Card>
              ))}
              {dirty && <Alert severity="info">Names changed. Analyze again before confirming.</Alert>}
              <Divider />
              {!!review.warnings.length && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acknowledged}
                      disabled={busy || dirty}
                      onChange={(event) => setAcknowledged(event.target.checked)}
                    />
                  }
                  label="I reviewed the warnings and want to proceed."
                />
              )}
              <Button
                variant="contained"
                size="large"
                disabled={
                  busy || dirty || names.some((name) => !name.trim()) || (!!review.warnings.length && !acknowledged)
                }
                onClick={() => request('upload')}
              >
                Confirm Upload
              </Button>
            </Stack>
          )}
          {results && (
            <Stack spacing={2}>
              <Typography variant="h5">Upload results</Typography>
              {results.map((result) => (
                <Alert
                  key={result.number}
                  severity={['Uploaded', 'Already uploaded'].includes(result.outcome) ? 'success' : 'warning'}
                >
                  {result.display_name}: {result.outcome}
                  {result.id && ` — ID ${result.id}`}
                  {result.status && ` (${result.status})`}
                  {result.message && ` — ${result.message}`}
                </Alert>
              ))}
              <Typography color="text.secondary">
                HasOffers uploads are verified individually. Email import uses the existing sync; SMS documents stay out
                of the email importer.
              </Typography>
              <Button
                disabled={busy}
                onClick={() => {
                  setResults(null);
                  setAcknowledged(false);
                  setDirty(true);
                }}
              >
                Review / retry
              </Button>
            </Stack>
          )}
          {busy && <Typography role="status">Working…</Typography>}
        </Stack>
      </Container>
    </Page>
  );
}
